import { FormControl, IconButton, InputAdornment } from "@mui/material";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { TextField } from "../../components";
import type { SxProps, Theme } from "@mui/material/styles";

type TextFieldProps = {
  label: string;
  name: string;
  type?:
    | "ssn"
    | "phone"
    | "text"
    | "digits"
    | "email"
    | "password"
    | "fein"
    | "zip"
    | "fax"
    | "number";
  fullWidth?: boolean;
  min?: number;
  max?: number;
  step?: number;
  error?: boolean;
  helperText?: string;
  defaultValue?: string;
  required?: boolean;
  customAriaLabel?: string;
  handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  disableEyeIcon?: boolean;
  sx?: SxProps<Theme>;
  nonEditable?: boolean;
};

const formatSSN = (v: string) =>
  v
    .replace(/\D/g, "")
    .replace(/^(\d{3})(\d)/, "$1-$2")
    .replace(/-(\d{2})(\d)/, "-$1-$2")
    .slice(0, 11);

const formatPhoneNumber = (value: string) => {
  value = value.replace(/\D/g, "");
  if (value.length > 3 && value.length <= 6) {
    return `(${value.slice(0, 3)}) ${value.slice(3)}`;
  } else if (value.length > 6) {
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
  }
  return value;
};

const formatFaxNumber = (value: string) => {
  value = value.replace(/\D/g, "");

  if (value.length > 3 && value.length <= 6) {
    return `(${value.slice(0, 3)}) ${value.slice(3)}`;
  } else if (value.length > 6) {
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
  }

  return value;
};

const formatFEIN = (v: string) =>
  v
    .replace(/\D/g, "")
    .replace(/^(\d{2})(\d)/, "$1-$2")
    .slice(0, 10);

const formatZip = (v: string) => v.replace(/\D/g, "").slice(0, 5);

const formatEmail = (v: string) => {
  if (!v) return "";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(v) ? "" : "Invalid email address.";
};

const MUITextField = ({
  label,
  name,
  type = "text",
  fullWidth = false,
  min,
  max,
  step,
  error = false,
  helperText = "",
  required = false,
  customAriaLabel,
  handleBlur,
  autoFocus = false,
  disabled = false,
  multiline = false,
  rows,
  maxLength = 40,
  defaultValue = "",
  disableEyeIcon = false,
  sx,
  nonEditable = false,
  
}: TextFieldProps) => {
  const { control } = useFormContext();
  const [validationError, setValidationError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleFormat = (value: string) => {
    let errorMsg = "";
    let formatted = value;

    if (!value) {
      setValidationError("");
      return value;
    }

    if (type === "ssn") {
      formatted = formatSSN(value);
      if (formatted.replace(/\D/g, "").length !== 9)
        errorMsg = "SSN must be exactly 9 digits.";
    } else if (type === "phone") {
      formatted = formatPhoneNumber(value);
      if (formatted.replace(/\D/g, "").length !== 10)
        errorMsg = "Phone number must be exactly 10 digits.";
    } else if (type === "digits") {
      formatted = value.replace(/\D/g, "");
    } else if (type === "fein") {
      formatted = formatFEIN(value);
      if (formatted.replace(/\D/g, "").length !== 9)
        errorMsg = "FEIN must be exactly 9 digits.";
    } else if (type === "zip") {
      formatted = formatZip(value);
      if (formatted.replace(/\D/g, "").length !== 5)
        errorMsg = "Zip code must be exactly 5 digits.";
    } else if (type === "email") {
      errorMsg = formatEmail(value);
    } else if (type === "text") {
      formatted = value.replace(/^\s+/, "");
    } else if (type == "fax") {
      formatted = formatFaxNumber(value);
      if (formatted.replace(/\D/g, "").length !== 10)
        errorMsg = "Fax number must be exactly 10 digits.";
    }

    setValidationError(errorMsg);
    return formatted;
  };

  const inputType =
    type === "password"
      ? showPassword
        ? "text"
        : "password"
      : type === "number"
        ? "number"
        : "text";

  return (
    <FormControl
      fullWidth={fullWidth}
      error={error || Boolean(validationError)}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: required ? `${label || name} is required` : false,
          maxLength: {
            value: type === "zip" ? 5 : maxLength,
            message: `${label || name} cannot exceed ${type === "zip" ? 5 : maxLength} characters`,
          },
          validate: (value) => {
            const trimmed = (value || "").trim();
            if (required && !trimmed) return `${label || name} is required`;
            if (!required && value && !trimmed)
              return "Spaces alone are not allowed";
            return true;
          },
        }}
        render={({ field }) => {
          const { onChange, value, ref } = field;

          return (
            <TextField
              label={
                <span>
                  {label}{" "}
                  {required && <span style={{ color: "#F44336" }}>*</span>}
                </span>
              }
              aria-label={label || customAriaLabel}
              variant="outlined"
              fullWidth={fullWidth}
              error={error || Boolean(validationError)}
              helperText={helperText || validationError}
              autoFocus={autoFocus}
              disabled={disabled}
              multiline={multiline}
              rows={multiline ? (rows ?? 4) : undefined}
              inputRef={ref}
              value={value || ""}
              onChange={(e) => {
                if (nonEditable) return;
                onChange(handleFormat(e.target.value));
              }}
              onBlur={handleBlur}
              sx={{
                "& .MuiInputLabel-asterisk": { color: "#F44336" },
                ...sx,
              }}
              inputProps={{
                maxLength: type === "zip" ? 5 : maxLength,
                inputMode:
                  type === "digits" || type === "zip" ? "numeric" : undefined,
                pattern:
                  type === "digits" || type === "zip" ? "[0-9]*" : undefined,
                min: type === "number" ? min : undefined,
                max: type === "number" ? max : undefined,
                step: type === "number" ? (step ?? 1) : undefined,
                readOnly: nonEditable,
              }}
              type={inputType}
              InputProps={
                type === "password" && !disableEyeIcon
                  ? {
                      readOnly: nonEditable,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
          );
        }}
      />
    </FormControl>
  );
};

export default MUITextField;
